/* ---------------------------------------------------------------------------
   Fetch-based SSE client with Last-Event-ID resume.

   Why fetch and not EventSource: EventSource cannot send an Authorization
   header, and this API authenticates every stream with a bearer token
   (openapi.yaml, convention 4).

   Duplicated from admin-dashboard/src/lib/sse.ts, which in turn adapted
   ../ox-report/dashboard/src/lib/sse.ts. There is no shared package by design.
   Three properties matter and none of them is optional here:

     1. LAST-EVENT-ID RESUME. Every frame carries `id: <cursor>`. The cursor of
        the last frame the caller actually PROCESSED is retained and sent as the
        `Last-Event-ID` header on reconnect, so the server replays from there and
        the reconnect has no gap and no duplicate. A stream that reconnects
        without this loses whatever arrived while the socket was down — on a run
        stream that is the difference between a ledger that ends at
        `run_finished` and one that hangs at `working` forever.
     2. RECONNECT WITH BACKOFF, honouring the server's `retry:` field, with full
        jitter so a platform restart does not bring every open console back in
        the same millisecond.
     3. `stream_reset` HANDLING. When the cursor is unknown or pruned the server
        says so rather than guessing. That is not an error: the caller discards
        local stream state, re-fetches the chat, and resumes from the cursor the
        frame carries.

   ONE THING TO INTERNALISE ON THIS SURFACE. The cursor is dense within the
   caller's VISIBILITY PROJECTION. An expert cursor and a client cursor are not
   interchangeable — they count different events — so a cursor from one must
   never be replayed against the other. This module is only ever pointed at
   `/expert/chats/{id}/stream`.
--------------------------------------------------------------------------- */
import { API_BASE, getAccessToken, refreshSession } from './api';

export interface SseFrame {
  /** The `id:` value. Store it after processing — it is the resume cursor. */
  id: string | null;
  /** The `event:` value, i.e. the event kind. */
  event: string;
  /** The joined `data:` lines. */
  data: string;
}

export interface StreamHandlers<TEvent> {
  /** One decoded event. Return normally; throwing does not kill the stream. */
  onEvent: (event: TEvent, frame: SseFrame) => void;
  /** Fired when the connection opens or reopens. */
  onOpen?: (attempt: number) => void;
  /** Fired when the socket drops. The client reconnects unless `fatal`. */
  onClose?: (info: { fatal: boolean; error?: unknown }) => void;
  /** The server could not replay from our cursor. Discard local stream state,
      re-fetch the chat, then let the stream continue from `fromCursor`. */
  onReset?: (fromCursor: string, reason: string) => void;
  /** A frame whose `data:` was not JSON. Worth reporting; never worth showing. */
  onMalformedFrame?: (frame: SseFrame) => void;
}

export interface StreamOptions<TEvent> extends StreamHandlers<TEvent> {
  /** Cursor to resume from on the FIRST connect — the `stream_cursor` the chat
      fetch handed back, which is how a page load avoids both a gap and a
      duplicate. */
  initialCursor?: string | null;
  /** Milliseconds. Overridden by the server's `retry:` field when it sends one. */
  reconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
}

export interface StreamHandle {
  close: () => void;
  /** The last cursor successfully handed to `onEvent`. */
  lastCursor: () => string | null;
}

const DEFAULT_RETRY_MS = 3000;
const MAX_RETRY_MS = 30_000;
const MAX_BUFFER_BYTES = 8 * 1024 * 1024;

/**
 * Opens an authenticated SSE stream. Returns a handle whose `close` is
 * idempotent and stops reconnection, so a component unmount cannot leave a
 * socket reconnecting into a dead renderer.
 */
export function openStream<TEvent = unknown>(path: string, options: StreamOptions<TEvent>): StreamHandle {
  const {
    onEvent,
    onOpen,
    onClose,
    onReset,
    onMalformedFrame,
    initialCursor = null,
    reconnectDelayMs = DEFAULT_RETRY_MS,
    maxReconnectDelayMs = MAX_RETRY_MS,
  } = options;

  let stopped = false;
  let controller: AbortController | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // The cursor of the last frame we finished processing. Not the last frame
  // received: a frame whose handler threw has not been processed, and resuming
  // past it would drop it permanently.
  let lastEventId: string | null = initialCursor;
  let serverRetryMs = reconnectDelayMs;
  let attempt = 0;

  const scheduleReconnect = () => {
    if (stopped) return;
    const base = Math.min(serverRetryMs * Math.max(1, 2 ** Math.max(0, attempt - 1)), maxReconnectDelayMs);
    const delay = Math.round(base / 2 + Math.random() * (base / 2));
    reconnectTimer = setTimeout(() => void connect(), delay);
  };

  const fetchOnce = (token: string | null, signal: AbortSignal) => {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (lastEventId) headers['Last-Event-ID'] = lastEventId;

    const url = new URL(`${API_BASE}${path}`, window.location.origin);
    // The contract's documented fallback for transports that strip the header.
    // The server prefers the header when both are present.
    if (lastEventId) url.searchParams.set('last_event_id', lastEventId);

    return fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers,
      signal,
      cache: 'no-store',
    });
  };

  const connect = async () => {
    if (stopped) return;
    reconnectTimer = null;
    attempt += 1;
    controller = new AbortController();
    const signal = controller.signal;

    try {
      let res = await fetchOnce(getAccessToken(), signal);

      // A review runs longer than an access token. A stream that dies at the
      // token boundary looks exactly like the platform dropping the run.
      if (res.status === 401) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          onClose?.({ fatal: true });
          stopped = true;
          return;
        }
        res = await fetchOnce(getAccessToken(), signal);
      }

      if (res.status === 401 || res.status === 403 || res.status === 404) {
        // Not transient — the claim was released, the assignment was removed,
        // or the chat is gone. Reconnecting would hammer the API forever.
        onClose?.({ fatal: true });
        stopped = true;
        return;
      }

      if (!res.ok || !res.body) {
        scheduleReconnect();
        return;
      }

      onOpen?.(attempt);
      attempt = 0;

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        // Guard against a server that never emits a frame separator: an
        // unbounded buffer is a memory leak that presents as a slow tab.
        if (buffer.length > MAX_BUFFER_BYTES) buffer = '';

        // Frames are separated by a blank line. Tolerate CRLF: some proxies
        // rewrite line endings, and a client that only splits on \n\n then
        // treats the whole stream as one unterminated frame.
        const normalized = buffer.replace(/\r\n/g, '\n');
        const chunks = normalized.split('\n\n');
        buffer = chunks.pop() ?? '';

        for (const chunk of chunks) {
          const frame = parseFrame(chunk);
          if (!frame) continue;

          if (frame.retryMs != null) serverRetryMs = frame.retryMs;
          if (frame.data === null) continue; // comment-only frame, e.g. `: keep-alive`

          const shape: SseFrame = { id: frame.id, event: frame.event, data: frame.data };

          let decoded: TEvent;
          try {
            decoded = JSON.parse(frame.data) as TEvent;
          } catch {
            onMalformedFrame?.(shape);
            continue; // a frame this client cannot read is not a reason to drop the stream
          }

          const kind = (decoded as { kind?: string } | null)?.kind;
          if (kind === 'stream_reset') {
            const reset = decoded as unknown as { from_cursor?: string; reason?: string };
            lastEventId = reset.from_cursor ?? null;
            onReset?.(reset.from_cursor ?? '', reset.reason ?? 'unknown_cursor');
            continue;
          }

          try {
            onEvent(decoded, shape);
          } catch {
            // A renderer fault must not take the transport down, and must not
            // advance the cursor past an event the renderer never applied.
            continue;
          }

          if (frame.id) lastEventId = frame.id;
        }
      }

      // A clean end of body mid-run is expected: the server closes with a grace
      // period on shutdown. Reconnect from the cursor rather than reporting a
      // failure that did not happen.
      onClose?.({ fatal: false });
      scheduleReconnect();
    } catch (error) {
      if (stopped || controller?.signal.aborted) return;
      onClose?.({ fatal: false, error });
      scheduleReconnect();
    }
  };

  void connect();

  return {
    close: () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller?.abort();
    },
    lastCursor: () => lastEventId,
  };
}

interface ParsedFrame {
  id: string | null;
  event: string;
  /** Null when the frame carried no `data:` line at all (a keep-alive comment). */
  data: string | null;
  retryMs: number | null;
}

export function parseFrame(chunk: string): ParsedFrame | null {
  if (!chunk.trim()) return null;

  let id: string | null = null;
  let event = 'message';
  let retryMs: number | null = null;
  const dataLines: string[] = [];

  for (const line of chunk.split('\n')) {
    if (!line || line.startsWith(':')) continue; // `: keep-alive` — required to be ignored
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // One optional leading space after the colon is part of the framing, not
    // the value. Trimming instead would corrupt a payload that legitimately
    // begins with whitespace.
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'id':
        id = value;
        break;
      case 'event':
        if (value) event = value;
        break;
      case 'data':
        dataLines.push(value);
        break;
      case 'retry': {
        const parsed = Number.parseInt(value, 10);
        if (Number.isFinite(parsed) && parsed > 0) retryMs = parsed;
        break;
      }
      default:
        break; // unknown fields are ignored, per spec
    }
  }

  return { id, event, data: dataLines.length ? dataLines.join('\n') : null, retryMs };
}
